import React, { useMemo, useState } from "react";
import * as ss from "simple-statistics";

import { api, useApi } from "../../services/api";
import { addDay, addMinute, formatDateTime, formatDay, formatWeekDay, getDateTime, getStockBeforeDay, startDay } from "../../services/date";

import Chart from "../components/Chart";

import {
    DateTime, AlpacaAccount, AlpacaPosition, Line,
    ClosedOrder, TradeCancelDB, TradeDB, PositionCloseDB, Trade, PortfolioHistory as PortfolioHistoryType
} from "../../../types";
import { TableInline } from "../components/Table";
import { Bar, getMinMax, Percent, USD } from "../components";

const dateStockBeforeDay = getStockBeforeDay();
const dateTodayStart = startDay(getDateTime());
const dateTodayEnd = addMinute(addDay(dateTodayStart, 1), -1);
const date30DaysAgo = addDay(dateTodayStart, -30);

export default function DashboardAlpaca() {
    const [account] = useApi.getAlpacaAccount({});
    const [portfolioHistory] = useApi.getAlpacaPortfolioHistory({ dateFrom: date30DaysAgo, dateTo: dateTodayEnd, timeFrame: "1D" });

    const [closedOrders] = useApi.getAlpacaClosedOrders({ dateFrom: dateStockBeforeDay, dateTo: dateTodayEnd });
    const [positions, , , , reloadP] = useApi.getAlpacaPositions({});
    const [positionsWithoutOpenedOrders, , , , reloadPWO] = useApi.getAlpacaPositionsWithoutOpenedOrders({});
    const [positionsClose] = useApi.getAlpacaPositionsClose({ dateFrom: dateTodayStart, dateTo: dateTodayEnd });

    const [ordersCancel] = useApi.getAlpacaOrdersCancel({ dateFrom: dateTodayStart, dateTo: dateTodayEnd });
    const [ordersCreate] = useApi.getAlpacaOrdersCreate({ dateFrom: dateTodayStart, dateTo: dateTodayEnd });

    function reloadPositions() {
        reloadP();
        reloadPWO();
    }

    return <div>
        <h1>Alpaca Dashboard</h1>
        {account && <Account {...account} />}
        {portfolioHistory && <PortfolioHistoryTable data={portfolioHistory} />}
        {portfolioHistory && <PortfolioHistory data={portfolioHistory} />}

        {closedOrders && <OrdersClosed orders={closedOrders} dateFrom={dateStockBeforeDay} />}
        {positions && <Positions title="Otevřené pozice" positions={positions} onChange={reloadPositions} />}
        {positionsWithoutOpenedOrders && <Positions title="Pozice bez otevřených objednávek" positions={positionsWithoutOpenedOrders} onChange={reloadPositions} />}
        {positionsClose && <PositionsClose positions={positionsClose} dateFrom={dateTodayStart} />}
        {ordersCancel && <OrdersCancel orders={ordersCancel} dateFrom={dateTodayStart} />}
        {ordersCreate && <OrdersCreate orders={ordersCreate} dateFrom={dateTodayStart} />}
    </div>
}

export function Account({ equity, cash, buying_power, multiplier, daytrade_count, ...account }: AlpacaAccount) {

    return <>
        <h3>Equity: <USD value={equity} /></h3>

        <div className="block">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Cash</th>
                        <th>Multiplier</th>
                        <th>Buying power</th>
                        <th>Day trade</th>
                        <th>Long market value</th>
                        <th>Short market value</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{account.status}</td>
                        <td><USD value={cash} /></td>
                        <td>{multiplier}</td>
                        <td><USD value={buying_power} /></td>
                        <td>{daytrade_count}</td>
                        <td><USD value={account.long_market_value} /></td>
                        <td><USD value={account.short_market_value} /></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </>;
}

function PortfolioHistory({ data }: { data: PortfolioHistoryType }) {
    const [y, setY] = useState<keyof PortfolioHistoryType[number]>("equity");

    const lines = useMemo(() => {
        const lines: Line[] = [];

        if (y === "equity") {
            lines.push({ y1: 100_000, color: "#FFFFFF88", label: "Původní kapitál" });
        } else {
            lines.push({ y1: 0, color: "#FFFFFF88", label: "Nula" });
        }

        for (let i = 1; i < data.length; i++) {
            const v1 = data[i - 1][y];
            const v2 = data[i][y];

            lines.push({
                x1: data[i - 1].timestamp,
                y1: v1,
                x2: data[i].timestamp,
                y2: v2,
                color: y === "equity" ? (v2 < v1 ? "green" : "red") : v1 >= 0 ? "green" : "red",
            });
        }

        return lines;
    }, [data, y]);

    const isYAxisPercent = y.includes("Percent");

    return <>
        {data[0] && Object.keys(data[0]).filter(k => k !== "timestamp").map(y => <button key={y} onClick={() => setY(y as any)}>{y}</button>)}
        <Chart height={500} lines={lines} isYAxisPercent={isYAxisPercent} />
    </>;
}

export function PortfolioHistoryTable({ data }: { data: PortfolioHistoryType }) {

    const [minEquity, maxEquity] = getMinMax(data.map(r => r.equity));
    const [minProfitLoss, maxProfitLoss] = getMinMax(data.map(r => r.profitLoss));
    const [minProfitClosedOrders, maxProfitClosedOrders] = getMinMax(data.map(r => r.profitClosedOrders));

    const firstRow = data[0];
    const rows7Days = data.filter(r => r.timestamp >= addDay(firstRow.timestamp, -7));
    const rows30Days = data.filter(r => r.timestamp >= addDay(firstRow.timestamp, -30));

    return <>
        <h2>Profit</h2>
        {firstRow && <div className="block">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th>Profit za poslední den</th>
                        <th>Profit za týden (7 dní)</th>
                        <th>Profit za měsíc (30 dní)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <USD value={firstRow?.profitLoss || 0} />
                            {" "}(<Percent value={firstRow?.profitLossPercent || 0} />)
                        </td>
                        <td>
                            <USD value={ss.sum(rows7Days.map(r => r.profitLoss))} />
                            {" "}(<Percent value={ss.sum(rows7Days.map(r => r.profitLossPercent))} />)
                        </td>
                        <td>
                            <USD value={ss.sum(rows30Days.map(r => r.profitLoss))} />
                            {" "}(<Percent value={ss.sum(rows30Days.map(r => r.profitLossPercent))} />)
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>}

        <TableInline
            rows={data}
            columns={[
                { title: "Date", key: "timestamp", render: "date" },
                { title: "Den", render: (row) => formatWeekDay(row.timestamp) },
                { title: "Closed Orders Profit", render: (row) => <Bar value={row.profitClosedOrders} percent={row.profitClosedOrdersPercent} min={minProfitClosedOrders} max={maxProfitClosedOrders} /> },
                { title: "Profit", render: (row) => <Bar value={row.profitLoss} percent={row.profitLossPercent} min={minProfitLoss} max={maxProfitLoss} /> },
                { title: "Equity", render: (row) => <Bar value={row.equity} min={minEquity} max={maxEquity} /> },
            ]}
        />
    </>
}




/*
export function PortfolioHistorySVG({ timestamp, profit_loss }: AlpacaPortfolioHistory) {

    const width = 600;
    const height = 300;

    const paddingLeft = 60;
    const paddingTop = 20;

    const xMin = ss.min(timestamp);
    const xMax = ss.max(timestamp);
    const yMin = ss.min(profit_loss);
    const yMax = ss.max(profit_loss);

    const scaleX = scale([xMin, xMax], [paddingLeft, width]);
    const scaleY = scale([yMin, yMax], [height, paddingTop]);

    const y0 = scaleY.getRange(0);

    const lines: { x1: number, x2: number, y1: number, y2: number, color: string, label: string }[] = [];
    for (let i = 1; i < timestamp.length; i++) {
        const v1 = profit_loss[i - 1];
        const v2 = profit_loss[i];

        lines.push({
            x1: scaleX.getRange(timestamp[i - 1]),
            y1: scaleY.getRange(v1),
            x2: scaleX.getRange(timestamp[i]),
            y2: scaleY.getRange(v2),
            color: v1 < v2 ? "green" : "red",
            label: v1.toLocaleString(),
        });
    }

    return <div className="block">
        <svg width={width} height={height} stroke='#000'>
            <line x1={paddingLeft} x2={paddingLeft} y1={0} y2={height} stroke="#000000A0" strokeWidth={1} />
            <text x={paddingLeft - 5} y={10} fontSize={12} textAnchor="end" stroke="#000">{yMax.toLocaleString()}</text>
            <text x={paddingLeft - 5} y={y0} fontSize={12} textAnchor="end" stroke="#000">0 USD</text>
            <text x={paddingLeft - 5} y={height - 10} fontSize={12} textAnchor="end" stroke="#000">{yMin.toLocaleString()}</text>

            <line x1={paddingLeft} x2={width} y1={y0} y2={y0} stroke="#000000A0" strokeWidth={1} />
            <text x={paddingLeft + 5} y={y0 + 15} fontSize={12} textAnchor="start" stroke="#000">{formatDay(xMin)}</text>
            <text x={width - 5} y={y0 + 15} fontSize={12} textAnchor="end" stroke="#000">{formatDay(xMax)}</text>

            {lines.map((line, i) => <line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke={line.color} strokeWidth={2} />)}
            {lines.map((line, i) => <text key={i} x={(line.x1 + line.x2) / 2} y={(line.y1 + line.y2) / 2 - 5} fontSize={12} textAnchor="middle" stroke="#000000A0">{line.label}</text>)}
        </svg>
    </div>;
}
*/

export function OrdersClosed({ orders, dateFrom }: { orders: ClosedOrder[], dateFrom: DateTime }) {
    return <>
        <h2>Uzavřené objednávky od {formatDay(dateFrom)}: <USD value={ss.sum(orders.map(o => o.gainPrice))} /></h2>
        <TableInline
            rows={orders}
            columns={[
                { title: "Symbol", key: "symbol", render: "string" },
                { title: "Qty", key: "qty", render: "number" },
                { title: "Date buy", key: "dateBuy", render: "datetime" },
                { title: "Date sell", key: "dateSell", render: "datetime" },
                { title: "Buy price", key: "buyPrice", render: "usd" },
                { title: "Sell price", key: "sellPrice", render: "usd" },
                { title: "Profit", key: "gainPrice", render: "usd" },
            ]}
        />
    </>;
}
export function Positions({ positions, title, onChange }: { title: string, positions: AlpacaPosition[], onChange?: (position: AlpacaPosition) => void }) {

    async function handleRemove(position: AlpacaPosition) {
        if (onChange) {
            await api.removeAlpacaPosition({ symbol: position.symbol, reason: "Liquidate from the Inv App." });
            onChange(position);
        }
    }

    return <>
        <h2>{title}: <USD value={ss.sum(positions.map(p => p.unrealized_pl))} /></h2>
        <TableInline
            rows={positions}
            columns={[
                { title: "Symbol", key: "symbol", render: "string" },
                { title: "Qty", key: "qty", render: "number" },
                { title: "Aktuální hodnota", key: "market_value", render: "usd" },
                { title: "Neuskutečněný profit", key: "unrealized_pl", render: "usd" },
                onChange ?
                    { title: "Akce", render: (row) => <button onClick={() => handleRemove(row)}>Likvidovat</button> } :
                    null
            ]}
        />
    </>;
}
export function PositionsClose({ positions, dateFrom }: { positions: PositionCloseDB[], dateFrom: DateTime }) {
    return <>
        <h2>Zavírané pozice od {formatDay(dateFrom)}: <USD value={ss.sum(positions.map(p => p.position?.unrealized_pl || 0))} /></h2>
        <TableInline
            rows={positions.map(p => ({ ...p.position, ...p }))}
            columns={[
                { title: "Symbol", key: "symbol", render: "string" },
                { title: "Date close", key: "dateClose", render: "datetime" },
                { title: "Profit", key: "unrealized_pl", render: "usd" },
                { title: "Reason", key: "reason", render: "string" },
                { title: "Error", key: "error", render: "json" },
            ]}
        />
    </>;
}


export function OrdersCancel({ orders, dateFrom }: { orders: TradeCancelDB[], dateFrom: DateTime }) {

    return <>
        <h2>Zavírané neuskutečněné objednávky od {formatDay(dateFrom)}: {orders.length}</h2>
        <TableInline
            rows={orders.map(o => ({ ...o.order, ...o }))}
            columns={[
                { title: "Symbol", key: "symbol" },
                { title: "Qty", key: "qty" },
                { title: "Reason", key: "reason" },
                { title: "Error", key: "error" }
            ]}
        />
    </>;
}
export function OrdersCreate({ orders, dateFrom }: { orders: TradeDB[], dateFrom: DateTime }) {
    return <>
        <h2>Nové objednávky od {formatDay(dateFrom)}: {orders.length}</h2>
        <TableInline
            rows={orders}
            columns={[
                { title: "Symbol", key: "symbol" },
                { title: "Qty", key: "qty" },
                { title: "Open", key: "openHigh", render: "usd" },
                { title: "Take profit", key: "takeProfitPercent", render: "percent" },
                { title: "Stop loss", key: "stopLossPercent", render: "percent" },
                { title: "Bid-Ask spread", key: "bidAskSpread", render: "usd" },
                { title: "Spread %", key: "bidAskSpreadPercent", render: "percent" },
                { title: "Is Quote", key: "isQuote" },
                { title: "Is Spread", key: "isBidAskSpread" },
                { title: "Is small spread", key: "isEnoughSpread" },
                { title: "Error", key: "error", render: "json" }
            ]}
        />
    </>;
}

export function Trades({ orders, dateFrom }: { orders: Trade[], dateFrom: DateTime }) {
    return <>
        <h2>{formatDay(dateFrom)}: {orders.length}</h2>
        <TableInline
            rows={orders}
            columns={[
                { title: "Symbol", key: "symbol" },
                { title: "Open", key: "openHigh", render: "usd" },
                { title: "Take profit", key: "takeProfit", render: "usd" },
                { title: "Stop loss", key: "stopLoss", render: "usd" },
                { title: "Bid-Ask spread", key: "bidAskSpread", render: "usd" },
                { title: "Spread %", key: "bidAskSpreadPercent", render: "percent" },
                { title: "Is Quote", key: "isQuote" },
                { title: "Is Spread", key: "isBidAskSpread" },
                { title: "Is small spread", key: "isEnoughSpread" },
            ]}
        />
    </>;
}

